import React, {useContext, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from "react-intl";

// MUI
import AccordionSummary from "@mui/material/AccordionSummary";
import Accordion from "@mui/material/Accordion";
import {Grid} from "@mui/material";
import AccordionDetails from "@mui/material/AccordionDetails";

// Components
import RightDrawer from "components/WMS/Mobile/RightDrawer";
import CustomButton from "components/CustomButton";
import BottomActionContainer from "components/WMS/Mobile/BottomActionContainer";

// Local components
import ShipPopup from "./components/ShipPopup";
import ShipmentCountBadge from "../ShipmentCountBadge";
import ShippedToast from "./components/ShippedToast";
import SelectedShipmentsCount from "./components/ContainerAccordion/SelectedShipmentsCount";
import ContainerName from "./components/ContainerAccordion/ContainerName";
import AccordionShipmentItem from "./components/ContainerAccordion/AccordionShipmentItem";
import ContainerCheckbox from "./components/ContainerAccordion/ContainerCheckbox";

// Assets
import {ReactComponent as ArrowDownIcon} from "assets/img/arrow-down.svg";
import {ReactComponent as CloseIcon} from "assets/img/wms/close_medium.svg";
import {grey300, greyishBlue50} from "assets/jss/main";

// Context
import {BarCodeScannerContext} from "contexts/barCodeScannerContext";
import {wmsShip} from "../../../../../api/shipment/wmsShip";
import {WMSContext} from "../../../context/WMSContext";
import {generateExpeditionList} from "../../../../../api/expeditionList/generate";
import {printExpeditionList} from "../../../../../api/expeditionList/print";
import pusherClient from "../../../../../utils/pusherClient";
import {ExpeditionListMessageSeverity, ExpeditionListMessageType} from "../../../../../utils/ExpeditionListMessageType";

ShippingMethodDrawer.propTypes = {
    shippingMethod: PropTypes.shape({
        shippingMethod: PropTypes.object.isRequired,
        containers: PropTypes.arrayOf(PropTypes.object).isRequired,
        shipmentCount: PropTypes.number.isRequired,
    }),
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    refreshShippingMethods: PropTypes.func.isRequired,
};

function ShippingMethodDrawer({shippingMethod, open, handleClose, refreshShippingMethods}) {
    const [selectedShipmentByContainer, setSelectedShipmentByContainer] = useState(null);
    const [openShipPopup, setOpenShipPopup] = useState(false);
    const [openToast, setOpenToast] = useState(false);
    const [totalSelectedShipments, setTotalSelectedShipments] = useState(0);

    const documentIsPrintedTimeout = useRef()

    const {scannedString} = useContext(BarCodeScannerContext);
    const {currentOrganization, preferredPrinter} = useContext(WMSContext)

    useEffect(() => {
        if (!selectedShipmentByContainer) {
           return;
        }

        setTotalSelectedShipments(getTotalSelectedShipments(selectedShipmentByContainer));
    }, [selectedShipmentByContainer]);

    // TODO: définir ce qui doit être scanné, sinon c'est à supprimer
    useEffect(() => {
        const shipmentAndContainer = findShipmentByTrackingNumber(scannedString);

        if (!shipmentAndContainer) {
            return;
        }

        selectShipment(shipmentAndContainer.shipmentId, shipmentAndContainer.containerId);
    }, [scannedString]);

    // TODO: définir ce qui doit être scanné, sinon c'est à supprimer
    function findShipmentByTrackingNumber(trackingNumber) {
        if (!shippingMethod || !shippingMethod.containers) {
            return null;
        }

        for (const container of shippingMethod.containers) {
            const shipment = container.shipments.find(shipment => shipment.trackingNumber === trackingNumber);

            if (shipment) {
                return {
                    shipmentId: shipment.id,
                    containerId: container.container.id,
                };
            }
        }

        return null;
    }

    function handleExpeditionListMessage(data) {
        clearTimeout(documentIsPrintedTimeout.current)
        if (ExpeditionListMessageSeverity.SUCCESS === data.severity) {
            setOpenShipPopup(false);
            setOpenToast(true);
            setSelectedShipmentByContainer(null);
            refreshShippingMethods();
        }
    }

    function ship() {
        if (!selectedShipmentByContainer) {
            return;
        }

        const selectedShipments = Object.values(selectedShipmentByContainer);
        const shipmentsToShip = selectedShipments.flat();

        wmsShip(shipmentsToShip).then(() => {
                generateExpeditionList(shipmentsToShip).then((response) => {
            if (currentOrganization.printExpeditionList) {
                    const currentExpeditionChannel = pusherClient.subscribe('expeditionList-'+response.expeditionListId)
                    currentExpeditionChannel.bind(ExpeditionListMessageType.PRINT_MESSAGE, (data) => handleExpeditionListMessage(data))

                    printExpeditionList(response.expeditionListId, preferredPrinter.documentPrinterId)
            }
                })
        })
    }

    function closeDrawer() {
        handleClose();
        setSelectedShipmentByContainer(null);
        setOpenShipPopup(false);
        setOpenToast(false);
    }

    function getTotalSelectedShipments(shipmentsByContainer) {
        return Object.values(shipmentsByContainer || {}).reduce((total, shipments) => total + shipments.length, 0);
    }

    function isShipmentSelected(shipmentId, containerId) {
        return selectedShipmentByContainer?.[containerId]?.includes(shipmentId) || false;
    }

    function containerHasSelectedShipments(containerId) {
        return selectedShipmentByContainer?.[containerId]?.length > 0;
    }

    function selectShipment(shipmentId, containerId) {
        setSelectedShipmentByContainer((prev) => {
            const newSelection = {...prev};

            if (!newSelection[containerId]) {
                newSelection[containerId] = [];
            }

            if (newSelection[containerId].includes(shipmentId)) {
                newSelection[containerId] = newSelection[containerId].filter(id => id !== shipmentId);
            } else {
                newSelection[containerId].push(shipmentId);
            }

            return newSelection;
        });
    }

    function selectAllShipments(containerId) {
        setSelectedShipmentByContainer((prev) => {
            const newSelection = {...prev};

            if (!newSelection[containerId]) {
                newSelection[containerId] = [];
            }

            const containerShipments = shippingMethod.containers.find(c => c.container.id === containerId)?.shipments || [];
            const shipmentIds = containerShipments.map(shipment => shipment.id);

            if (newSelection[containerId].length === shipmentIds.length) {
                newSelection[containerId] = [];
            } else {
                newSelection[containerId] = shipmentIds;
            }

            return newSelection;
        });
    }

    if (shippingMethod === null || shippingMethod === undefined) {
        return null;
    }

    return (
        <RightDrawer
            open={open}
            handleClose={closeDrawer}
            headerLeftContent={shippingMethod.shippingMethod.name}
            headerRightContent={(
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                    <ShipmentCountBadge count={shippingMethod.shipmentCount} />
                    <CloseIcon onClick={closeDrawer} />
                </div>
            )}
        >
            <Grid
                container
                direction={"column"}
                justifyContent={'space-between'}
                flexWrap={"nowrap"}
                sx={{
                    height: "100%",
                    overflow: "hidden"
                }}
            >
                <Grid
                    container
                    direction={"column"}
                    sx={{
                        overflow: "auto"
                    }}
                >
                    {shippingMethod.containers.map((container) => (
                        <Accordion
                            key={container.container.id}
                            sx={{
                                alignSelf: 'stretch',
                                boxShadow: 'none',
                                '&.MuiAccordion-root': {
                                    overflow:"auto",
                                    borderTop: `1px solid ${greyishBlue50}`,
                                    '::before': {
                                        display: 'none', // remove the default border
                                    },
                                    '&.Mui-expanded': {
                                        margin: 0
                                    }
                                }
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ArrowDownIcon />}
                                sx={{
                                    height: '80px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    background: '#FFF'
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '100%',
                                        justifyContent: 'space-between'
                                    }}
                                >
                                    <div>
                                        <ContainerCheckbox
                                            checked={containerHasSelectedShipments(container.container.id)}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                selectAllShipments(container.container.id)
                                            }}
                                        />
                                        <ContainerName name={container.container.name} />
                                    </div>
                                    <SelectedShipmentsCount
                                        count={selectedShipmentByContainer?.[container.container.id]?.length || 0}
                                        total={container.shipments.length}
                                    />
                                </div>
                            </AccordionSummary>
                            <AccordionDetails sx={{backgroundColor: grey300, padding: 0}}>
                                <Grid
                                    container
                                    direction={"column"}
                                    sx={{
                                        overflow: "auto"
                                    }}
                                >
                                    {container?.shipments && container.shipments.map((shipment) => (
                                        <AccordionShipmentItem
                                            key={shipment.id}
                                            onClick={() => selectShipment(shipment.id, container.container.id)}
                                            checked={isShipmentSelected(shipment.id, container.container.id)}
                                            trackingNumber={shipment.trackingNumber}
                                            orderId={shipment.order.externalId}
                                        />
                                    ))}
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Grid>
               <BottomActionContainer>
                   <Grid item xs>
                       <CustomButton
                           disabled={!totalSelectedShipments}
                           fullWidth={true}
                           thicker={true}
                           onClick={() => setOpenShipPopup(true)}
                       >
                           {totalSelectedShipments > 0 ? (
                               <FormattedMessage
                                   id='wms.shipping.action.ship.shipments'
                                   values={{
                                       totalSelectedShipments: totalSelectedShipments
                                   }}
                               />
                           ) : (
                               <FormattedMessage id='wms.shipping.action.ship.disabled' />
                           )}
                       </CustomButton>
                   </Grid>
               </BottomActionContainer>
            </Grid>
            <ShipPopup
                open={openShipPopup}
                handleClose={() => setOpenShipPopup(false)}
                onClick={() =>  {
                    documentIsPrintedTimeout.current = setTimeout(() => {
                        setOpenShipPopup(false);
                        setOpenToast(true);
                        setSelectedShipmentByContainer(null);
                        refreshShippingMethods();
                    }, 5000)
                    ship()
                }}
                totalShipments={totalSelectedShipments}
            />
            <ShippedToast
                isOpen={openToast}
                handleClose={() => setOpenToast(false)}
            />
        </RightDrawer>
    );
}

export default ShippingMethodDrawer;
